import type { AxiosInstance } from 'axios';
import { useEffect, useRef, useState } from 'react';
import type { ResumableExtraction } from '../OnboardingContext';
import type { ExtractedSection, SlotSurfaceForms } from '../onboarding-api';
import type { AsyncJobData } from './useAsyncJobPolling';
import { useAsyncJobPolling } from './useAsyncJobPolling';

interface UseResumableJobBannerOptions {
  resumableJob?: ResumableExtraction | null;
  /** Skip the Resume/Discard prompt and start polling immediately -- the
   * user already chose Resume elsewhere (the global banner). */
  autoResume?: boolean;
  onAutoResumeHandled?: () => void;
  onExtractionStateChange?: (isExtracting: boolean) => void;
  onRulesExtracted: (sections: Array<ExtractedSection>, slots?: SlotSurfaceForms) => void;
  onResumeError: (message: string) => void;
  clearResumableExtraction: () => void;
  discardResumableExtraction: (jobId: string) => Promise<boolean>;
}

/**
 * Groups the state and handlers for OnboardingUpload's resume/discard banner
 * -- kept as its own hook rather than inline so the resulting component stays
 * under the cognitive-complexity limit and this logic is unit-testable on
 * its own.
 */
export function useResumableJobBanner(
  bbAxios: AxiosInstance,
  options: UseResumableJobBannerOptions
) {
  const {
    resumableJob,
    autoResume,
    onAutoResumeHandled,
    onExtractionStateChange,
    onRulesExtracted,
    onResumeError,
    clearResumableExtraction,
    discardResumableExtraction,
  } = options;
  const [resumeBannerDismissed, setResumeBannerDismissed] = useState(false);
  const [resumingJobId, setResumingJobId] = useState<string | null>(null);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [discardError, setDiscardError] = useState<string | null>(null);
  const hasHandledAutoResume = useRef(false);

  const isAwaitingAutoResume = autoResume && !hasHandledAutoResume.current;
  const showResumeBanner =
    !!resumableJob && !resumeBannerDismissed && !resumingJobId && !isAwaitingAutoResume;

  const handleResumeComplete = (result: AsyncJobData['result']) => {
    setResumingJobId(null);
    onExtractionStateChange?.(false);
    clearResumableExtraction();
    onRulesExtracted(result?.sections ?? [], result?.slot_surface_forms);
  };

  const handleResumeError = (message: string) => {
    setResumingJobId(null);
    onExtractionStateChange?.(false);
    onResumeError(message);
  };

  const { percentComplete: resumePercent, message: resumeMessage } = useAsyncJobPolling(bbAxios, {
    jobId: resumingJobId,
    onComplete: handleResumeComplete,
    onError: handleResumeError,
  });

  const handleResume = () => {
    if (!resumableJob) {
      return;
    }
    onExtractionStateChange?.(true);
    setResumingJobId(resumableJob.jobId);
  };

  // The user already chose Resume on the global banner before this component
  // mounted -- start polling immediately instead of asking again. Guarded by
  // a ref (not just `autoResume`) so a later re-render with the same prop
  // value can't restart a poll the user has since discarded.
  useEffect(() => {
    if (!autoResume || hasHandledAutoResume.current) {
      return;
    }
    hasHandledAutoResume.current = true;
    if (resumableJob) {
      onExtractionStateChange?.(true);
      setResumingJobId(resumableJob.jobId);
    }
    onAutoResumeHandled?.();
  }, [autoResume, resumableJob, onExtractionStateChange, onAutoResumeHandled]);

  const handleDiscard = async () => {
    if (!resumableJob) {
      return;
    }
    setIsDiscarding(true);
    setDiscardError(null);
    const didCancel = await discardResumableExtraction(resumableJob.jobId);
    // Only dismiss on a confirmed cancel -- a failed request must not make a
    // still-running job look discarded.
    setResumeBannerDismissed(didCancel);
    setDiscardError(didCancel ? null : 'Could not discard the job. Please try again.');
    setIsDiscarding(false);
  };

  return {
    showResumeBanner,
    resumingJobId,
    resumePercent,
    resumeMessage,
    isDiscarding,
    discardError,
    handleResume,
    handleDiscard,
  };
}
